import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FilterSection } from "../../../components/patients/FilterSection";
import { PatientListTable } from "../../../components/patients/PatientListTable";
import { FilterModal } from "../../../components/patients/FilterModal";
import { useToast } from "@/hooks/use-toast";
import { usePatientDetailsForGroups } from "@/hooks/patient/usePatientDetailsForGroups";
import { usePatientGroups } from "@/hooks/activity/usePatientGroups";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { API_CONFIG, getStandardHeaders } from "@/config/envConfig";

export default function RemovePatients() {
  const { patientGroupId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasuraToken, user, token } = useAuth();
  const { patientGroups } = usePatientGroups();

  // Filter states
  const [nameValue, setNameValue] = useState("");
  const [phoneValue, setPhoneValue] = useState("");
  const [emrValue, setEmrValue] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState<any>({});
  const [isRemoving, setIsRemoving] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);

  const itemsPerPage = 10;

  // Get current group data
  const currentGroup = useMemo(() => {
    return patientGroups.find(g => g.patient_group_id.toString() === patientGroupId);
  }, [patientGroups, patientGroupId]);

  // Fetch patients for this specific group
  const { 
    patients, 
    isLoading, 
    totalCount, 
    refetch 
  } = usePatientDetailsForGroups(
    itemsPerPage, 
    (currentPage - 1) * itemsPerPage,
    patientGroupId ? parseInt(patientGroupId) : undefined
  );

  // Transform patients data for display
  const displayPatients = useMemo(() => {
    return patients.map(patient => ({
      id: patient.enrollment_patient_id?.toString() || '',
      patientName: `${patient.first_name || ''} ${patient.middle_name || ''} ${patient.last_name || ''}`.trim(),
      emr: patient.emr_id || 'N/A',
      gender: patient.gender || 'N/A',
      dateOfBirth: patient.dob || 'N/A',
      age: patient.dob ? Math.floor((new Date().getTime() - new Date(patient.dob).getTime()) / 31557600000) : 0,
      phoneNumber: patient.mobile_number || patient.home_number || 'N/A',
      providerDetails: patient.provider_name || 'N/A',
      clinicDetails: patient.clinic_name || 'N/A',
      primaryDx: 'N/A',
      secondaryDx: 'N/A',
      insuranceType: 'N/A',
    }));
  }, [patients]);

  // Pagination logic
  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handleSearch = () => {
    setCurrentPage(1);
    
    // Build basic search filters
    const conditions: any[] = [];
    
    if (nameValue) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameValue}%` } },
          { last_name: { _ilike: `%${nameValue}%` } }
        ]
      });
    }
    if (phoneValue) {
      conditions.push({ mobile_number: { _ilike: `%${phoneValue}%` } });
    }
    if (emrValue) {
      conditions.push({ emr_id: { _ilike: `%${emrValue}%` } });
    }
    
    // Add advanced filters from FilterModal
    if (appliedFilters.clinic_id?.length > 0) {
      conditions.push({ clinic_id: { _in: appliedFilters.clinic_id } });
    }
    if (appliedFilters.clinic_facility_id?.length > 0) {
      conditions.push({ clinic_facility_id: { _in: appliedFilters.clinic_facility_id } });
    }
    if (appliedFilters.provider_id?.length > 0) {
      conditions.push({ provider_id: { _in: appliedFilters.provider_id } });
    }
    if (appliedFilters.primary_insurance_payor?.length > 0) {
      conditions.push({ primary_insurance_payor: { _in: appliedFilters.primary_insurance_payor } });
    }
    if (appliedFilters.secondary_insurance_payor?.length > 0) {
      conditions.push({ secondary_insurance_payor: { _in: appliedFilters.secondary_insurance_payor } });
    }
    if (appliedFilters.condition?.length > 0) {
      conditions.push({ condition: { _in: appliedFilters.condition } });
    }
    if (appliedFilters.zip_code?.length > 0) {
      conditions.push({ zip_code: { _in: appliedFilters.zip_code } });
    }
    if (appliedFilters.program_eligibility?.length > 0) {
      conditions.push({ program_eligibility: { _in: appliedFilters.program_eligibility } });
    }
    
    // Combine all conditions
    const searchFilters = conditions.length > 0 ? { _and: conditions } : {};
    
    refetch(searchFilters);
  };

  const handleFilter = () => {
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = (filters: any) => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    
    // Build where clause immediately with the new filters
    const conditions: any[] = [];
    
    // Add basic search filters
    if (nameValue) {
      conditions.push({
        _or: [
          { first_name: { _ilike: `%${nameValue}%` } },
          { last_name: { _ilike: `%${nameValue}%` } }
        ]
      });
    }
    if (phoneValue) {
      conditions.push({ mobile_number: { _ilike: `%${phoneValue}%` } });
    }
    if (emrValue) {
      conditions.push({ emr_id: { _ilike: `%${emrValue}%` } });
    }
    
    // Add advanced filters from FilterModal
    if (filters.clinic_id?.length > 0) {
      conditions.push({ clinic_id: { _in: filters.clinic_id } });
    }
    if (filters.clinic_facility_id?.length > 0) {
      conditions.push({ clinic_facility_id: { _in: filters.clinic_facility_id } });
    }
    if (filters.provider_id?.length > 0) {
      conditions.push({ provider_id: { _in: filters.provider_id } });
    }
    if (filters.primary_insurance_payor?.length > 0) {
      conditions.push({ primary_insurance_payor: { _in: filters.primary_insurance_payor } });
    }
    if (filters.secondary_insurance_payor?.length > 0) {
      conditions.push({ secondary_insurance_payor: { _in: filters.secondary_insurance_payor } });
    }
    if (filters.condition?.length > 0) {
      conditions.push({ condition: { _in: filters.condition } });
    }
    if (filters.zip_code?.length > 0) {
      conditions.push({ zip_code: { _in: filters.zip_code } });
    }
    if (filters.program_eligibility?.length > 0) {
      conditions.push({ program_eligibility: { _in: filters.program_eligibility } });
    }
    
    // Combine all conditions
    const searchFilters = conditions.length > 0 ? { _and: conditions } : {};
    
    refetch(searchFilters);
  };

  const handlePatientSelect = (patientId: string) => {
    setSelectedPatients(prev => 
      prev.includes(patientId)
        ? prev.filter(id => id !== patientId)
        : [...prev, patientId]
    );
  };

  const handleSelectAll = () => {
    const allCurrentPageIds = displayPatients.map(p => p.id);
    const allSelected = allCurrentPageIds.every(id => selectedPatients.includes(id));
    
    if (allSelected) {
      setSelectedPatients(prev => prev.filter(id => !allCurrentPageIds.includes(id)));
    } else {
      setSelectedPatients(prev => [...new Set([...prev, ...allCurrentPageIds])]);
    }
  };

  const handleRemovePatients = async () => {
    if (selectedPatients.length === 0) {
      toast({
        title: "No patients selected",
        description: "Please select patients to remove from the group.",
        variant: "destructive",
      });
      return;
    }

    if (selectedPatients.length >= totalCount) {
      toast({
        title: "Cannot remove all patients",
        description: "At least 1 patient must remain in the group. Please deselect some patients.",
        variant: "destructive",
      });
      return;
    }

    if (!currentGroup || !user || !token) {
      toast({
        title: "Error",
        description: "Missing required information to remove patients.",
        variant: "destructive",
      });
      return;
    }

    setIsRemoving(true);

    try {
      const patientIdsToRemove = selectedPatients.map(id => parseInt(id));
      
      const payload = {
        groupName: currentGroup.group_name,
        isArchived: false,
        patientIdsToAdd: [],
        patientIdsToRemove: patientIdsToRemove
      };

      const url = `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${patientGroupId}`;
      
      await axios.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      toast({
        title: "Patients removed",
        description: `Successfully removed ${selectedPatients.length} patient(s) from the group.`,
      });

      // Navigate back to edit page
      navigate(`/admin/patient-groups/edit/${patientGroupId}`);
    } catch (error) {
      console.error('Error removing patients:', error);
      toast({
        title: "Error",
        description: "Failed to remove patients from the group.",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      <header className="bg-background border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/admin/patient-groups/edit/${patientGroupId}`)}
              className="p-2"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold text-foreground">Remove Patients</h1>
          </div>
          <Button 
            onClick={handleRemovePatients}
            disabled={selectedPatients.length === 0 || isRemoving}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 className="h-4 w-4" />
            {isRemoving ? 'Removing...' : `Remove Selected (${selectedPatients.length})`}
          </Button>
        </div>
      </header>

      <main className="p-6 space-y-6">
        {/* Filter Section */}
        <FilterSection
          nameValue={nameValue}
          phoneValue={phoneValue}
          emrValue={emrValue}
          onNameChange={setNameValue}
          onPhoneChange={setPhoneValue}
          onEmrChange={setEmrValue}
          onSearch={handleSearch}
          onFilter={handleFilter}
        />

        {/* Patients Section */}
        <div className="bg-background border rounded-lg p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-foreground mb-2">
              Select patients to remove from group
            </h3>
            <p className="text-sm text-muted-foreground">
              Choose the patients you want to remove from this group. Selected patients will be permanently removed from the group.
            </p>
          </div>

          <PatientListTable
            patients={displayPatients}
            selectedPatients={selectedPatients}
            onPatientSelect={handlePatientSelect}
            onSelectAll={handleSelectAll}
            filteredCount={totalCount}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </div>
      </main>

      <FilterModal
        open={isFilterModalOpen}
        onOpenChange={setIsFilterModalOpen}
        onApply={handleApplyFilters}
      />
    </div>
  );
}